import * as React from 'react';

import { ErrorBoundary } from 'bernie-view';
import { LocalizedText } from 'bernie-l10n';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';

import {
  AllAmenitiesViewModel,
  HighlightAmenitiesViewModel,
  HotelMapModel,
  LocationSummaryViewModel,
  OverviewViewModel,
  PrimaryPageMessagingModel,
  PropertyAdditionalInfoViewModel,
  PropertyInfoPriceGuaranteeViewModel,
  PropertyInfoSectionViewModel,
  ReviewsViewModel,
  ScrollTarget,
  StickyOfferModel
} from 'models';
import { VrPropertySummaryViewModel } from 'models/hotel/hotel-details-types';

import { UitkCardTextSegment } from 'uitk-react-cards';

// Components
import { Information } from 'components/infosite/property-details/information';
import { Breadcrumbs } from './breadcrumbs';
import { HotelAmenities } from './components/hotel-amenities-summary';
import { HotelReviews } from './components/hotel-reviews-summary';
import { MapAndLocationDescription } from 'components/infosite/property-details/map-and-location-description';
import { PaymentInstallmentMessage } from 'components/infosite/property-details/payment-installments';
import { PropertyBadges } from 'components/infosite/property-details/property-badges';

export interface HotelInfoProps {
  additionalInfo?: PropertyAdditionalInfoViewModel;
  amenities?: AllAmenitiesViewModel;
  hotelName?: string;
  englishHotelName?: string;
  mapModel?: HotelMapModel;
  overview?: OverviewViewModel;
  primaryPageMessagingModel?: PrimaryPageMessagingModel;
  reviews?: ReviewsViewModel;
  starRatingIcon?: string;
  stars?: string;
  singleUnitVrPropertySummary?: VrPropertySummaryViewModel;
  vipMessage?: string;
  stickyOffer?: StickyOfferModel;
  priceGuarantee?: PropertyInfoPriceGuaranteeViewModel;
  roomsAndRatesTarget?: ScrollTarget;
  withName: boolean;
  currentHotel?: CurrentHotelStore;
  propertyTypeBadge?: string; // Experiment: 32599 Property_Type_Badging
}

// tslint:disable-next-line:cyclomatic-complexity
export const HotelInfo: React.FunctionComponent<HotelInfoProps> = ({
  additionalInfo,
  amenities,
  hotelName,
  englishHotelName,
  mapModel,
  overview,
  primaryPageMessagingModel,
  reviews,
  starRatingIcon,
  stars,
  singleUnitVrPropertySummary,
  vipMessage,
  stickyOffer,
  priceGuarantee,
  roomsAndRatesTarget,
  withName,
  currentHotel,
  propertyTypeBadge
}) => {
  const propertyAmenities: PropertyInfoSectionViewModel = additionalInfo && additionalInfo.propertyAmenitiesSection;
  const locationSummary: LocationSummaryViewModel = additionalInfo && additionalInfo.locationSummary;
  const highlightAmenities: HighlightAmenitiesViewModel[] = amenities && amenities.highlightAmenities;
  const hasReviews: boolean = Boolean(reviews && reviews.count) && reviews.count > 0;
  const hasTopAmenities: boolean = Boolean(amenities && amenities.topSummary) && amenities.topSummary.length > 0;
  const hasPrimaryPropertyMessaging: boolean = Boolean(
    additionalInfo &&
      additionalInfo.primaryPropertyMessaging &&
      additionalInfo.primaryPropertyMessaging.title &&
      additionalInfo.primaryPropertyMessaging.body
  );
  const cardTextSegmentProps = {
    className: 'all-x-padding-three',
    separator: UitkCardTextSegment.Separator.BOTTOM
  };
  const walkScoreModel = (currentHotel.hotel && currentHotel.hotel.locationInfo && currentHotel.hotel.locationInfo.walkScore) || {};

  return (
    <React.Fragment>
      <Breadcrumbs breadcrumbsContent={currentHotel.breadcrumbs} isDateless={currentHotel.searchCriteria.isDateless} />
      {hotelName && withName && (
        <UitkCardTextSegment {...cardTextSegmentProps}>
          <ErrorBoundary label="hotel.information.details.information">
            <PropertyBadges vipMessage={vipMessage} propertyTypeBadge={propertyTypeBadge} />
            <Information
              singleUnitVrPropertySummary={singleUnitVrPropertySummary}
              name={hotelName}
              englishName={englishHotelName}
              stars={stars}
              starRatingIcon={starRatingIcon}
              stickyOffer={stickyOffer}
              priceGuarantee={priceGuarantee}
              target={roomsAndRatesTarget}
              currentHotel={currentHotel}
            />
            <h2 className="is-visually-hidden">
              <LocalizedText message="hotels.infosite.information.a11yLabel" />
            </h2>
          </ErrorBoundary>
          <PaymentInstallmentMessage primaryPageMessagingModel={primaryPageMessagingModel} />
        </UitkCardTextSegment>
      )}
      {hasReviews && (
        <UitkCardTextSegment {...cardTextSegmentProps}>
          <HotelReviews reviews={reviews} header="hotels.infosite.reviews.a11yLabel" />
        </UitkCardTextSegment>
      )}
      {hasTopAmenities && (
        <UitkCardTextSegment className="uitk-card-content-scrollable" {...cardTextSegmentProps}>
          <HotelAmenities
            amenities={amenities}
            highlightAmenities={highlightAmenities}
            propertyAmenities={propertyAmenities}
            header="hotels.infosite.amenities.a11yLabel"
          />
        </UitkCardTextSegment>
      )}
      {hasPrimaryPropertyMessaging && (
        <UitkCardTextSegment {...cardTextSegmentProps}>
          <h2 className="all-b-padding-one">{additionalInfo.primaryPropertyMessaging.title}</h2>
          <div className="pwa-theme--grey-700">{additionalInfo.primaryPropertyMessaging.body}</div>
        </UitkCardTextSegment>
      )}
      <MapAndLocationDescription
        hotelName={hotelName}
        locationSummary={locationSummary}
        mapModel={mapModel}
        overview={overview}
        walkScoreModel={walkScoreModel}
      />
    </React.Fragment>
  );
};
